import React, { useState, useEffect, useRef, useCallback } from "react";
import "../styles/pages/_about.scss";
import { AboutHeroParagraph, AboutPersonalParagraphs, AboutFunFactsList } from './home/sections/AboutContent';
import beachPhoto from '../assets/beach.webp';
import scenicPhoto from '../assets/scenic_view.webp';
import familyPhoto from '../assets/family.webp';
import drawingPhoto from '../assets/drawing.webp';
import chowPhoto from '../assets/chow.webp';
import momPhoto from '../assets/mom.webp';
import beachPhotoPng from '../assets/beach.png';
import scenicPhotoPng from '../assets/scenic_view.png';
import familyPhotoPng from '../assets/family.png';
import drawingPhotoPng from '../assets/drawing.png';
import chowPhotoPng from '../assets/chow.png';
import momPhotoPng from '../assets/mom.png';


// Static image metadata — defined outside component so useCallback deps stay stable
const imageData: Record<string, { title: string; description: string; category: string; icon: string }> = {
  "Me and My Cat": {
    title: "My Cat & Me",
    description: "Hanging out with my first cat pet Chow",
    category: "personal",
    icon: "🐱"
  },
  "Family fishing trip": {
    title: "Family Trip",
    description: "All men fishing trip with the family, sadly we didn't catch any fish ",
    category: "family",
    icon: "🎣"
  },
  "Scenic View": {
    title: "Hiking in Yosemite",
    description: "A friends trip to Yosemite's Mist Trail, defineitly one for the books!",
    category: "hobby",
    icon: "🥾⛰️"
  },
  "My artwork": {
    title: "Artwork",
    description: "Pencil drawing I created last summer",
    category: "hobby",
    icon: "🎨"
  }
};

const About = () => {
  // Enhanced tooltip state with more properties
  const [tooltip, setTooltip] = useState({
    text: "",
    title: "",
    visible: false,
    position: { top: 0, left: 0 },
    direction: "right", // Changed to right/left instead of top/bottom
    category: "",
    icon: "📷",
  });

  // Keep track of which image is being hovered
  const [hoveredImage, setHoveredImage] = useState<HTMLImageElement | null>(null);

  // Reference for tooltip element to apply animations
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Debounce timer ref to throttle tooltip updates on rapid hover
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // Handle mouse enter event for images with inside tooltip positioning (debounced 50ms)
  const handleImageMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLImageElement>, alt: string) => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      const target = event.currentTarget;
      debounceTimerRef.current = setTimeout(() => {
        const rect = target.getBoundingClientRect();
        const isLeftSide = target.className.includes("left");
        const direction = isLeftSide ? "right" : "left";
        const imageInfo = imageData[alt] || {
          title: alt,
          description: "Image description",
          category: "photo",
          icon: "📷"
        };
        setHoveredImage(target);
        let position;
        if (isLeftSide) {
          position = {
            left: rect.right + 15,
            top: rect.top + (rect.height / 2) + window.scrollY
          };
        } else {
          position = {
            left: rect.left - 15,
            top: rect.top + (rect.height / 2) + window.scrollY
          };
        }
        setTooltip({
          text: imageInfo.description,
          title: imageInfo.title,
          category: imageInfo.category,
          icon: imageInfo.icon,
          visible: true,
          direction,
          position,
        });
      }, 50);
    },
    []
  );

  // Handle mouse leave event for images — cancel pending debounce
  const handleImageMouseLeave = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setTooltip(prev => ({ ...prev, visible: false }));
    setHoveredImage(null);
  }, []);

  return (
    <div className="about-page">
      {/* Enhanced Inside Tooltip */}
      {tooltip.visible && (
        <div
          ref={tooltipRef}
          className={`image-tooltip ${tooltip.direction}`}
          style={{
            position: 'absolute',
            top: `${tooltip.position.top - 30}px`, // Center vertically
            left: tooltip.direction === 'right' ?
              `${tooltip.position.left}px` :
              `${tooltip.position.left - 220}px`, // Adjust based on direction and width
            zIndex: 1000,
          }}
        >
          <div className="tooltip-content">
            <span className="tooltip-icon">{tooltip.icon}</span>
            <div>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{tooltip.title}</div>
              <div className="tooltip-text">{tooltip.text}</div>
            </div>
          </div>

          {/* Arrow pointing to the image */}
          <div className={`tooltip-arrow ${tooltip.direction}`}></div>
        </div>
      )}

      {/* Hero Section */}
      <section className="hero-about">
        <div className="hero-content-about">
          <h1>About Me</h1>
          <AboutHeroParagraph />
        </div>
      </section>

      {/* Personal Life Section */}
      <section className="personal-life">
        <picture>
          <source srcSet={scenicPhoto} type="image/webp" />
          <img
            src={scenicPhotoPng}
            alt="Scenic View"
            loading="lazy"
            className={`edge-image top-left ${hoveredImage && hoveredImage.alt === "Scenic View" ? "pulse" : ""}`}
            onMouseEnter={(e) => handleImageMouseEnter(e, "Scenic View")}
            onMouseLeave={handleImageMouseLeave}
          />
        </picture>
        <picture>
          <source srcSet={familyPhoto} type="image/webp" />
          <img
            src={familyPhotoPng}
            alt="Family fishing trip"
            loading="lazy"
            className={`edge-image bottom-right ${hoveredImage && hoveredImage.alt === "Family fishing trip" ? "pulse" : ""}`}
            onMouseEnter={(e) => handleImageMouseEnter(e, "Family fishing trip")}
            onMouseLeave={handleImageMouseLeave}
          />
        </picture>
        <div className="content">
          <h2>Who I Am</h2>
          <AboutPersonalParagraphs />
        </div>
      </section>

      {/* Fun Facts Section */}
      <section className="fun-facts">
        <picture>
          <source srcSet={chowPhoto} type="image/webp" />
          <img
            src={chowPhotoPng}
            alt="Me and My Cat"
            loading="lazy"
            className={`edge-image top-left ${hoveredImage && hoveredImage.alt === "Me and My Cat" ? "pulse" : ""}`}
            onMouseEnter={(e) => handleImageMouseEnter(e, "Me and My Cat")}
            onMouseLeave={handleImageMouseLeave}
          />
        </picture>
        <picture>
          <source srcSet={drawingPhoto} type="image/webp" />
          <img
            src={drawingPhotoPng}
            alt="My artwork"
            loading="lazy"
            className={`edge-image top-right ${hoveredImage && hoveredImage.alt === "My artwork" ? "pulse" : ""}`}
            onMouseEnter={(e) => handleImageMouseEnter(e, "My artwork")}
            onMouseLeave={handleImageMouseLeave}
          />
        </picture>
        <div className="content">
          <h2>Fun Facts</h2>
          <AboutFunFactsList />
        </div>
      </section>
    </div>
  );
};

export default About;
